/**
 * Main analytics export
 * Provides a unified interface for analytics throughout the app
 */

import { initializeAnalytics, getAnalytics } from './core';
import type { AnalyticsConfig } from './types';

// Export types
export type {
  AnalyticsEvent,
  FinancialEvent,
  EngagementEvent,
  ConversionEvent,
  PerformanceEvent,
  ErrorEvent,
  UserTraits,
  ConsentPreferences,
  EventCategory,
  AmountRange,
} from './types';

// Export privacy utilities
export {
  hashIdentifier,
  anonymizeAmount,
  sanitizeDescription,
  sanitizePayee,
  sanitizeErrorMessage,
} from './privacy';

// Export consent utilities
export {
  getConsentPreferences,
  saveConsentPreferences,
  hasConsent,
  hasConsentFor,
  acceptAllConsent,
  rejectNonEssentialConsent,
  updateConsentPreference,
  clearConsent,
} from './consent';

// Import getConsentPreferences for use in this file
import { getConsentPreferences } from './consent';

// Default configuration
const defaultConfig: AnalyticsConfig = {
  debug: process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true',
  providers: {
    ga4: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ? {
      measurementId: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
    } : undefined,
    plausible: process.env.NEXT_PUBLIC_PLAUSIBLE_DOMAIN ? {
      domain: process.env.NEXT_PUBLIC_PLAUSIBLE_DOMAIN,
    } : undefined,
  },
  consent: {
    cookieName: process.env.ANALYTICS_CONSENT_COOKIE_NAME || 'bb-analytics-consent',
    expiryDays: 365,
  },
};

// Initialize analytics on import (client-side only)
if (typeof window !== 'undefined') {
  initializeAnalytics(defaultConfig);
}

/**
 * Main analytics object
 */
export const analytics = {
  /**
   * Track a custom event
   */
  track: async (event: string, properties?: Record<string, any>) => {
    const service = getAnalytics();
    if (!service) return;
    
    await service.track({
      event,
      category: properties?.category || 'engagement',
      properties,
    });
  },

  /**
   * Identify a user
   */
  identify: async (userId: string, traits?: Record<string, any>) => {
    const service = getAnalytics();
    if (!service) return;
    
    await service.identify(userId, traits);
  },

  /**
   * Track a page view
   */
  page: async (name?: string, properties?: Record<string, any>) => {
    const service = getAnalytics();
    if (!service) return;
    
    await service.page(name, properties);
  },

  /**
   * Track an error
   */
  error: async (error: Error | string, context?: Record<string, any>) => {
    const service = getAnalytics();
    if (!service) return;
    
    await service.trackError(error, context);
  },

  /**
   * Track performance metric
   */
  performance: async (metricName: string, value: number, unit: 'ms' | 'seconds' | 'bytes' | 'percent' = 'ms') => {
    const service = getAnalytics();
    if (!service) return;
    
    await service.trackPerformance(metricName, value, unit);
  },

  /**
   * Track revenue
   */
  revenue: async (amount: number, currency: string = 'USD', properties?: Record<string, any>) => {
    const service = getAnalytics();
    if (!service) return;
    
    await service.track({
      event: 'revenue',
      category: 'conversion',
      properties: {
        revenue: amount,
        currency,
        ...properties,
      },
    });
  },

  /**
   * Reset analytics (for logout)
   */
  reset: async () => {
    const service = getAnalytics();
    if (!service) return;
    
    await service.reset();
  },

  /**
   * Debug: Check analytics state
   */
  debug: () => {
    const service = getAnalytics();
    if (!service) {
      console.log('[Analytics Debug] No service instance');
      return;
    }
    
    console.log('[Analytics Debug] Service state:', {
      hasService: !!service,
      providers: (service as any).providers?.length || 0,
      config: (service as any).config,
      consent: getConsentPreferences(),
    });
  },

  /**
   * Force reinitialize analytics (useful after consent change)
   */
  reinitialize: () => {
    console.log('[Analytics] Reinitializing...');
    initializeAnalytics(defaultConfig);
  },
};

// Re-export hooks
export { useAnalytics } from './hooks';

// Re-export event modules
export { authEvents } from './events/auth';
export { financialEvents } from './events/financial';
export { engagementEvents } from './events/engagement';
export { conversionEvents } from './events/conversion';

// Helper function to track Web Vitals
export function trackWebVitals(metric: { name: string; value: number }): void {
  analytics.performance(metric.name, Math.round(metric.value), 'ms');
}